import React, { useEffect, useState, useCallback } from 'react'

import { Tabs } from '@hm-group/fabric-components'
import { PillsList, LayoutContainer } from '@pb/components'
import { getLabelFromStorage, isNorthstarBrand, getScreenSize } from '@pb/utils'
import { useFeature } from '@optimizely/react-sdk'
import { useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'

import appSelectors from 'App/duck/selectors'
import optimizelyConstants from 'constants/optimizely'
import FavouritesItemsList from 'features/favourites/components/FavouritesItemList'
import MyProfileOverview from 'features/myProfile'
import { RootState } from 'redux/types'

import { ExtendedPills, Pills, Tab } from '../../types'
import Following from './components/Following'
import MyCloset from './components/MyCloset'
import MyStyleboards from './components/MyStyleboards'
import { trackPill } from './helpers/tracking'
import styles from './Stylehub.scss'
import { getFavouritesItems } from 'features/favourites/duck/selectors'

const Stylehub: React.FC = () => {
  const shouldEnabledNS = isNorthstarBrand && process.env.NODE_ENVIRONMENT === 'development'
  const { search } = useLocation()
  const history = useHistory()
  const queryParams = new URLSearchParams(search)
  const appState = useSelector((state: RootState) => state)

  const isValidatedUser = useSelector(appSelectors.getValidatedUser)
  const shouldShowMyProfile =
    process.env.IS_MY_PROFILE_STYLEHUB_ENABLED === 'enabled' && isValidatedUser
  const isStyleboardHybrisEnabled = useSelector(appSelectors.getIsStyleboardEnabled)
  const isStyleboardsEnabled =
    process.env.IS_STYLEBOARD_ENABLED === 'enabled' && isStyleboardHybrisEnabled

  const isSignedIn = useSelector(appSelectors.getIsSignedIn)
  const [sections, setSections] = useState<Tab[]>([])
  const [pills, setPills] = useState<Pills[]>([])
  const [childrenData, setChildrenData] = useState<ExtendedPills[]>([])
  const [selectedSectionId, setSelectedSectionId] = useState<string | number | undefined>('')

  const [isMyCloset, { myclosetflag }] = useFeature(optimizelyConstants.MY_CLOSET_FLAG)
  const showMycloset =
    (isMyCloset && myclosetflag) || myclosetflag === localStorage.getItem('myclosetflag')
  const isMyClosetEnabled = process.env.IS_MY_CLOSET_ENABLED === 'enabled' && showMycloset
  const favouritesItems = useSelector((state: RootState) => getFavouritesItems(state))
  const isSmallScreen = ['small', 'xsmall'].includes(getScreenSize())

  const setUpPills = (): ExtendedPills[] => {
    const possiblePills: ExtendedPills[] = [
      {
        id: 0,
        trackingLabel: 'saved',
        path: 'saved',
        title: getLabelFromStorage('styleboard.saved'),
        hasCondition: isStyleboardsEnabled || isMyClosetEnabled,
        children: <FavouritesItemsList />,
      },
      {
        id: 1,
        trackingLabel: 'following',
        path: 'following',
        title: getLabelFromStorage('styleboard.following.title'),
        hasCondition: isStyleboardsEnabled,
        children: <Following />,
      },
      {
        id: 2,
        trackingLabel: 'styleboards',
        path: 'styleboards',
        title: getLabelFromStorage('styleboard.my.styleboards'),
        hasCondition: isStyleboardsEnabled,
        children: <MyStyleboards />,
      },
      {
        id: 3,
        trackingLabel: 'closet',
        path: 'closet',
        title: getLabelFromStorage('mycloset.tab.title'),
        hasCondition: isMyClosetEnabled,
        children: <MyCloset />,
      },
    ]

    const filteredActivePills: ExtendedPills[] = possiblePills.filter((i) => i.hasCondition)
    setChildrenData(filteredActivePills)

    return filteredActivePills
  }

  const setPillIdtoQueryParams = (nameID: string): void => {
    const isPillIdInQueries = queryParams.has('pill')
    if (isPillIdInQueries) {
      queryParams.set('pill', nameID)
    } else {
      queryParams.append('pill', nameID)
    }
  }

  // Set the clicked pill id to the query parameter /?pill=pillId
  const setQueryParams = (pillId: string | number | undefined): void => {
    if (pillId !== undefined) {
      if (typeof pillId === 'number') {
        const activePills = setUpPills()
        const getClickedPill = activePills.find((pill) => pill.id === pillId)
        if (getClickedPill) {
          setPillIdtoQueryParams(getClickedPill.trackingLabel)
        }
      } else {
        setPillIdtoQueryParams(pillId)
      }
    }
    history.replace({ search: queryParams.toString() })
  }

  const onPillClick = (pressedPill: string | number | undefined): void => {
    setQueryParams(pressedPill)
    return setSelectedSectionId(pressedPill)
  }
  const onPillClickCallback = useCallback((activeId) => onPillClick(activeId), [])

  useEffect(() => {
    const activePills = setUpPills()

    // Get the active pill id form query parameter /?pill=pillId
    const pillId = search.includes('?pill=') && search.replace('?pill=', '')
    const isPillAvailable = activePills?.some((pill) => pill.trackingLabel === pillId)
    if (shouldEnabledNS) {
      const sectionInfo = activePills.find((section) => section.trackingLabel === pillId)
      const setDefaultSelectedPill = activePills[0]?.id || 0
      setSelectedSectionId(isPillAvailable ? sectionInfo?.id : setDefaultSelectedPill)
    } else {
      const setDefaultSelectedPill = activePills[0]?.trackingLabel || ''
      setSelectedSectionId(isPillAvailable ? (pillId as string) : setDefaultSelectedPill)
    }
  }, [isStyleboardsEnabled, isSignedIn, isMyClosetEnabled])

  const changeDefaultActiveSection = (): number | undefined => {
    const preChoosenSection = search.includes('?pill=') && search.replace('?pill=', '')
    if (preChoosenSection) {
      const possibleDefaults = {
        saved: 0,
        following: 1,
        styleboards: 2,
      }
      return possibleDefaults[preChoosenSection]
    }
    return undefined
  }

  useEffect(() => {
    const activeSections = setUpPills()
    if (shouldEnabledNS) {
      const sectionToShow: Tab[] = []

      activeSections?.map((tab) => {
        const { title, id } = tab

        const noOfFavouriteItems = favouritesItems.length === 0 ? '' : `(${favouritesItems.length})`
        return sectionToShow.push({
          label: id === 0 ? `${title} ${noOfFavouriteItems}` : title,
          id,
          content: <></>,
        })
      })
      setSections(sectionToShow)
    } else {
      const pillsToShow: Pills[] = []

      activeSections?.map((pill) => {
        const { title, trackingLabel, path } = pill

        return pillsToShow.push({
          title,
          trackingLabel,
          path,
          selected: trackingLabel === selectedSectionId,
          role: 'tab',
        })
      })
      setPills(pillsToShow)
    }
  }, [selectedSectionId, favouritesItems])

  useEffect(() => {
    if (selectedSectionId !== undefined && appState.favourites.isFavouritesLoaded) {
      const activePills = setUpPills()
      const selectedPillInfo = activePills.find((pill) =>
        typeof selectedSectionId === 'number'
          ? selectedSectionId === pill.id
          : selectedSectionId === pill.trackingLabel,
      )
      if (selectedPillInfo) {
        trackPill(selectedPillInfo.trackingLabel, appState)
      }
    }
  }, [selectedSectionId, appState.favourites.isFavouritesLoaded])

  return (
    (isStyleboardsEnabled || isMyClosetEnabled) && (
      <>
        {shouldEnabledNS ? (
          <>
            <div className={styles.tabsWrapper}>
              <Tabs
                variant={isSmallScreen ? 'clustered' : 'fixed'}
                onClick={onPillClickCallback}
                tabs={sections}
                className={isSmallScreen ? '' : styles.tabsContainer}
                defaultActiveTab={changeDefaultActiveSection()}
              />
            </div>

            {childrenData.map((data) => {
              return (
                <div
                  hidden={data.id !== selectedSectionId}
                  key={data.trackingLabel}
                  id={`panel-${data?.trackingLabel}`}
                  aria-labelledby={`panel-${data?.trackingLabel}`}
                >
                  {data.children}
                </div>
              )
            })}
          </>
        ) : (
          <LayoutContainer large className={styles.container}>
            {shouldShowMyProfile && <MyProfileOverview />}
            {pills.length > 0 && (
              <>
                <div className={styles.pillContainer} role="tablist">
                  <PillsList list={pills} onClick={onPillClickCallback} />
                </div>
                {childrenData.map((data) => {
                  return (
                    <div
                      hidden={data.trackingLabel !== selectedSectionId}
                      key={data.trackingLabel}
                      id={`panel-${data?.trackingLabel}`}
                      role="tabpanel"
                      tabIndex={0}
                      aria-labelledby={`panel-${data?.trackingLabel}`}
                    >
                      {data.children}
                    </div>
                  )
                })}
              </>
            )}
          </LayoutContainer>
        )}
      </>
    )
  )
}

export default Stylehub
